import { useState, useEffect, useCallback, useRef } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback banners (same as DB seed) shown until the API responds
const DEFAULT_BANNERS = [
    // Apple Collection - MacBook closed lid premium shot
    {
      id: 1,
      type: 'brand',
      brand: 'Apple',
      title: 'The Latest from Apple',
      subtitle: 'Premium Collection',
      products: ['iPhone 17 Pro Max', 'MacBook Pro M4', 'AirPods Pro', 'Apple Watch'],
      description: 'Premium Apple devices delivered from Dubai. Experience the ecosystem.',
      image: 'https://images.unsplash.com/photo-1737947640001-54765a2b0287?auto=format&fit=crop&w=1600&q=80',
      cta: 'Shop Apple',
      ctaLink: '/products?category=mobiles',
      bgColor: 'bg-slate-900'
    },
    // Samsung Collection
    {
      id: 2,
      type: 'brand',
      brand: 'Samsung',
      title: 'Galaxy Ecosystem',
      subtitle: 'AI Revolution',
      products: ['Galaxy S25 Ultra', 'Galaxy Watch 7', 'Galaxy Buds 3 Pro', 'Galaxy Tab'],
      description: 'The future is here with Galaxy AI. Complete Samsung collection available.',
      image: 'https://images.pexels.com/photos/16149966/pexels-photo-16149966.jpeg?auto=compress&cs=tinysrgb&w=1600',
      cta: 'Shop Samsung',
      ctaLink: '/products?category=mobiles',
      bgColor: 'bg-slate-100',
      textDark: true
    },
    // PlayStation Gaming
    {
      id: 3,
      type: 'brand',
      brand: 'PlayStation',
      title: 'Next-Gen Gaming',
      subtitle: 'Play Has No Limits',
      products: ['PS5 Pro Console', 'DualSense Edge', 'PlayStation VR2', 'Pulse Elite'],
      description: 'Ultimate gaming experience. All PlayStation accessories in one place.',
      image: 'https://images.pexels.com/photos/13189290/pexels-photo-13189290.jpeg?auto=compress&cs=tinysrgb&w=1600',
      cta: 'Shop Gaming',
      ctaLink: '/products?category=gaming',
      bgColor: 'bg-slate-900'
    },
    // Luxury Watches - Rolex
    {
      id: 4,
      type: 'brand',
      brand: 'Luxury Watches',
      title: 'Timeless Elegance',
      subtitle: 'Certified Authentic',
      products: ['Rolex Submariner', 'Omega Seamaster', 'TAG Heuer', 'Cartier'],
      description: 'Authentic luxury timepieces from Dubai. Every watch certified genuine.',
      image: 'https://images.pexels.com/photos/6230455/pexels-photo-6230455.jpeg?auto=compress&cs=tinysrgb&w=1600',
      cta: 'Shop Watches',
      ctaLink: '/products?category=watches',
      bgColor: 'bg-black'
    },
    // Nike Sneakers
    {
      id: 5,
      type: 'brand',
      brand: 'Nike',
      title: 'Just Do It',
      subtitle: 'Latest Collection',
      products: ['Air Max 2025', 'Jordan 1 Retro', 'Nike Dunk Low', 'Tech Fleece'],
      description: 'Authentic Nike products. Sneakers, apparel & accessories from Dubai.',
      image: 'https://images.unsplash.com/photo-1611858447198-c6b3cc794943?auto=format&fit=crop&w=1600&q=80',
      cta: 'Shop Nike',
      ctaLink: '/products?category=shoes',
      bgColor: 'bg-slate-800'
    },
    // Premium Audio - Headphones
    {
      id: 6,
      type: 'brand',
      brand: 'Premium Audio',
      title: 'Sound Perfection',
      subtitle: 'Studio Quality',
      products: ['Sony WH-1000XM5', 'Bose QC Ultra', 'AirPods Max', 'Sennheiser'],
      description: 'World-class audio equipment. Headphones, speakers & soundbars.',
      image: 'https://images.pexels.com/photos/2919003/pexels-photo-2919003.jpeg?auto=compress&cs=tinysrgb&w=1600',
      cta: 'Shop Audio',
      ctaLink: '/products?category=electronics',
      bgColor: 'bg-white',
      textDark: true
    },
    // Luxury Perfumes
    {
      id: 7,
      type: 'brand',
      brand: 'Fragrances',
      title: 'Luxury Perfumes',
      subtitle: 'Dubai Exclusive',
      products: ['Dior Sauvage', 'Chanel No.5', 'Tom Ford Oud', 'Creed Aventus'],
      description: 'Authentic designer fragrances. The finest perfumes from Dubai.',
      image: 'https://images.unsplash.com/photo-1770301410072-f6ef6dad65b2?auto=format&fit=crop&w=1600&q=80',
      cta: 'Shop Perfumes',
      ctaLink: '/products?category=perfumes',
      bgColor: 'bg-amber-50',
      textDark: true
    },
    // Dubai to India Delivery - Cargo plane
    {
      id: 8,
      type: 'service',
      title: 'Dubai to India',
      subtitle: '3-Day Express Delivery',
      description: 'Fast cross-border courier shipping from Dubai to your doorstep in India.',
      image: 'https://images.pexels.com/photos/32563053/pexels-photo-32563053.jpeg?auto=compress&cs=tinysrgb&w=1600',
      cta: 'Learn More',
      ctaLink: '/delivery-info',
      bgColor: 'bg-sky-600'
    },
    // Personal Shopping - Dubai Mall
    {
      id: 9,
      type: 'service',
      title: 'Personal Shopping',
      subtitle: 'On-Demand from Dubai',
      description: "Can't find what you need? Our personal shoppers in Dubai will source it for you.",
      image: 'https://images.unsplash.com/photo-1761474257877-47bac32b03b6?auto=format&fit=crop&w=1600&q=80',
      cta: 'Request Item',
      ctaLink: '/request-item',
      bgColor: 'bg-slate-800'
    }
  ];

const BannerCarousel = () => {
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [banners, setBanners] = useState(DEFAULT_BANNERS);

  useEffect(() => {
    axios.get(`${API}/banners`)
      .then((res) => {
        if (Array.isArray(res.data) && res.data.length > 0) setBanners(res.data);
      })
      .catch(() => {});
  }, []);

  const autoplayPlugin = useRef(
    Autoplay({ 
      delay: 5000, 
      stopOnInteraction: false,
      stopOnMouseEnter: true,
      playOnInit: true,
      rootNode: (emblaRoot) => emblaRoot.parentElement
    })
  );
  
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { 
      loop: true, 
      containScroll: 'trimSnaps',
      dragFree: false,
      skipSnaps: false,
      align: 'start'
    },
    [autoplayPlugin.current]
  );

  useEffect(() => {
    if (emblaApi) emblaApi.reInit();
  }, [banners, emblaApi]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollPrev();
      const autoplay = emblaApi.plugins()?.autoplay;
      if (autoplay) autoplay.reset();
    }
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) {
      emblaApi.scrollNext();
      const autoplay = emblaApi.plugins()?.autoplay;
      if (autoplay) autoplay.reset();
    }
  }, [emblaApi]);

  const scrollTo = useCallback((index) => {
    if (emblaApi) {
      emblaApi.scrollTo(index);
      const autoplay = emblaApi.plugins()?.autoplay;
      if (autoplay) autoplay.reset();
    }
  }, [emblaApi]);

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    
    onSelect();
    emblaApi.on('select', onSelect);
    
    const autoplay = emblaApi.plugins()?.autoplay;
    if (autoplay && !autoplay.isPlaying()) {
      autoplay.play();
    }
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        const autoplay = emblaApi.plugins()?.autoplay;
        if (autoplay) autoplay.play();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      emblaApi.off('select', onSelect);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [emblaApi, onSelect]);

  return (
    <div className="relative w-full select-none bg-white" data-testid="banner-carousel">
      {/* Full-width on mobile, contained on desktop */}
      <div className="md:max-w-7xl md:mx-auto md:px-6 md:py-4">
        <div className="relative md:rounded-2xl overflow-hidden md:shadow-lg">
          <div className="overflow-hidden cursor-grab active:cursor-grabbing" ref={emblaRef}>
            <div className="flex touch-pan-y backface-hidden">
              {banners.map((banner, index) => (
                <div key={banner.id} className="flex-[0_0_100%] min-w-0">
                  <div className={`relative h-[200px] sm:h-[280px] md:h-[340px] lg:h-[400px] ${banner.bgColor} overflow-hidden`}>
                    {/* Background Image */}
                    <img
                      src={banner.image || undefined}
                      alt={banner.title}
                      className="absolute inset-0 w-full h-full object-cover object-center"
                      loading={index === 0 ? "eager" : "lazy"}
                      draggable="false"
                    />
                    
                    {/* Gradient Overlay */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${
                      banner.textDark 
                        ? 'from-white/90 via-white/70 to-white/30' 
                        : 'from-black/80 via-black/60 to-black/20'
                    }`} />

                    {/* Content */}
                    <div className="relative h-full flex items-center px-4 sm:px-10 md:px-14 lg:px-16">
                      <div className="max-w-xl space-y-1.5 sm:space-y-3 md:space-y-4">
                        {/* Brand/Service Tag */}
                        <span className={`inline-block text-[9px] sm:text-xs font-bold uppercase tracking-wider px-2 py-1 sm:px-3 sm:py-1.5 rounded ${
                          banner.textDark 
                            ? 'text-slate-700 bg-slate-900/10 backdrop-blur-sm' 
                            : 'text-white/90 bg-white/20 backdrop-blur-sm'
                        }`}>
                          {banner.brand || banner.subtitle}
                        </span>
                        
                        {/* Subtitle (only for brand banners - hidden on mobile) */}
                        {banner.type === 'brand' && (
                          <p className={`hidden sm:block text-xs sm:text-sm font-medium ${banner.textDark ? 'text-slate-600' : 'text-white/70'}`}>
                            {banner.subtitle}
                          </p>
                        )}
                        
                        {/* Title */}
                        <h2 className={`text-xl sm:text-3xl md:text-4xl lg:text-5xl font-outfit font-bold leading-tight ${
                          banner.textDark ? 'text-slate-900' : 'text-white'
                        }`}>
                          {banner.title}
                        </h2>
                        
                        {/* Products List (brand banners - hidden on mobile) */}
                        {banner.products && (
                          <div className="hidden sm:flex flex-wrap gap-2 pt-1">
                            {banner.products.map((product, idx) => (
                              <span 
                                key={`${banner.id}-product-${idx}`}
                                className={`text-xs px-2.5 py-1 rounded-full ${
                                  banner.textDark 
                                    ? 'bg-slate-900/10 text-slate-700' 
                                    : 'bg-white/15 text-white/90'
                                }`}
                              >
                                {product}
                              </span>
                            ))}
                          </div>
                        )}
                        
                        {/* Description - hidden on mobile */}
                        <p className={`text-sm md:text-base max-w-md hidden sm:block ${
                          banner.textDark ? 'text-slate-600' : 'text-white/80'
                        }`}>
                          {banner.description}
                        </p>
                        
                        {/* CTA Button */}
                        <Button
                          size="default"
                          className={`font-semibold shadow-lg mt-1 sm:mt-2 h-9 sm:h-12 px-4 sm:px-8 text-xs sm:text-base ${
                            banner.textDark 
                              ? 'bg-slate-900 text-white hover:bg-slate-800' 
                              : 'bg-white text-slate-900 hover:bg-white/90'
                          }`}
                          onClick={() => navigate(banner.ctaLink)}
                          data-testid={`banner-cta-${banner.id}`}
                        >
                          {banner.cta}
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Navigation Arrows - Hidden on mobile */}
          <button
            onClick={scrollPrev}
            className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-white rounded-full items-center justify-center transition-all shadow-lg"
            aria-label="Previous slide"
            data-testid="carousel-prev"
          >
            <ChevronLeft className="w-6 h-6 text-slate-700" />
          </button>
          <button
            onClick={scrollNext}
            className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 bg-white/90 hover:bg-white rounded-full items-center justify-center transition-all shadow-lg"
            aria-label="Next slide"
            data-testid="carousel-next"
          >
            <ChevronRight className="w-6 h-6 text-slate-700" />
          </button>

          {/* Dots Navigation - Smaller on mobile */}
          <div className="absolute bottom-2 sm:bottom-4 left-1/2 -translate-x-1/2 flex gap-1 sm:gap-1.5 z-10">
            {banners.map((banner, index) => (
              <button
                key={`dot-${banner.id}`}
                className={`rounded-full transition-all duration-300 flex-shrink-0 ${
                  index === selectedIndex
                    ? 'bg-white w-4 sm:w-6 h-1.5 sm:h-2'
                    : 'bg-white/50 w-1.5 sm:w-2 h-1.5 sm:h-2 hover:bg-white/70'
                }`}
                onClick={() => scrollTo(index)}
                aria-label={`Go to slide ${index + 1}`}
                data-testid={`carousel-dot-${index}`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BannerCarousel;
